import { Injectable } from '@angular/core';
import { CheckedDataService, ICheckedEntity } from '@se/se.platform.base';
import {
    IDataSource,
    IServerDataSource,
    IServerDataSourceList,
    IDataSourceItem,
    IDataSourceQuery,
} from '@se/cp.common';
import { DataBinding, Logger } from '@se/se.platform.base';
import { isEmpty, mapValues, zipObject, differenceWith } from 'lodash-es';

interface IInternalDataSource {
    items: IDataSource;
    ids: string[];
}

@Injectable({
    providedIn: 'root',
})
export class CpDatasourceService implements CheckedDataService {
    private sources: Record<string, IInternalDataSource> = {};

    constructor(private logger: Logger) {}

    augmentItem(item: IDataSourceItem) {
        if (!item.getPropertyPrimitiveValue) {
            Object.defineProperty(item, 'getPropertyPrimitiveValue', {
                value: this.unboundPropertyGetter,
            });
        }

        return item;
    }

    getById(id: string, additionalItems?: IDataSourceItem[], queries?: IDataSourceQuery[]): IDataSource | undefined {
        if (!id) {
            return undefined;
        }

        const dataSource = this.sources[id];
        if (!dataSource) {
            this.logger.warn(`Unknown dataSource: ${id}`);
            return undefined;
        }
        let items = this.getItemsWithAdditionalItems(dataSource, additionalItems);

        return this.applyQueries(items, queries);
    }

    getEntities(binding: DataBinding): ICheckedEntity[] {
        return this.getById(binding.toString()) || [];
    }

    getSelectedEntity(_: DataBinding): ICheckedEntity | undefined {
        return undefined;
    }

    setDataSources<T extends string[]>(serverSources: IServerDataSource<T>) {
        const transformedSources = mapValues(serverSources, (source) => {
            let dataSource: IDataSource;
            if (source.isTree) {
                return this.transformTree(source.props, source.items);
            }

            dataSource = source.items.map((item) => this.createItem(source.props, item));

            return {
                items: dataSource,
                ids: dataSource.map((i) => i.Id),
            };
        });

        this.sources = transformedSources;
    }

    private transformTree<T extends string[]>(props: string[], items: IServerDataSourceList<T>): IInternalDataSource {
        return items.reduce(
            (acc, item) => {
                const [treeNode, childrenIds] = this.createTreeItem(props, item);
                acc.ids = acc.ids.concat(childrenIds);
                acc.items.push(treeNode);
                return acc;
            },
            {
                items: [],
                ids: [],
            } as IInternalDataSource
        );
    }

    private createItem(props: string[], values: string[]): IDataSourceItem {
        const obj: Partial<IDataSourceItem> = zipObject(props, values);

        return this.augmentItem(obj as IDataSourceItem);
    }

    private createTreeItem(props: string[], values: string[]): [IDataSourceItem, string[]] {
        const treeNode = this.createItem(props, values);
        const children = (treeNode as any).Children;
        let allIds: string[] = [treeNode.Id];

        if (!isEmpty(children)) {
            const transformedChildren = this.transformTree(props, children);
            treeNode.children = transformedChildren.items;
            allIds = allIds.concat(transformedChildren.ids);
        }

        return [treeNode, allIds];
    }

    // Per migliorare un minimo le performance
    private unboundPropertyGetter = function (this: IDataSourceItem, propertyName: keyof IDataSourceItem) {
        return this[propertyName];
    };

    private getItemsWithAdditionalItems(dataSource: IInternalDataSource, additionalItems?: IDataSourceItem[]) {
        if (isEmpty(additionalItems)) {
            return dataSource.items;
        }

        const itemsToAdd = differenceWith(additionalItems, dataSource.ids, (a, b) => a.Id === b).map((item) =>
            this.augmentItem(item)
        );

        return itemsToAdd.concat(dataSource.items);
    }

    private applyQueries(items: IDataSourceItem[], queries?: IDataSourceQuery[]) {
        if (!queries) {
            return items;
        }

        for (const query of queries) {
            if (query.type === 'where') {
                items = items.filter((item) => item.getPropertyPrimitiveValue(query.propertyName) === query.value);
            }
        }

        return items;
    }
}
